import MiniSearch from "minisearch";
import type { City, Composer, Org } from "../types";

export let composerSearch: MiniSearch;
export let citySearch: MiniSearch;
export let orgSearch: MiniSearch;

// Search is name-based only: the search box resolves to a composer / city / org
// from these static indexes, which then becomes a server-side filter. There is no
// client-side full-text concert index anymore (the corpus lives in D1).
export function buildSearchIndex(
  composersData: Composer[],
  citiesData: City[],
  orgsData: Org[]
) {
  // Composer search index
  composerSearch = new MiniSearch({
    fields: ["name", "sort_name"],
    storeFields: ["id", "name", "sort_name", "count"],
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
      boost: { name: 2 },
    },
  });
  composerSearch.addAll(composersData);

  // City search index
  citySearch = new MiniSearch({
    fields: ["name", "country_name"],
    storeFields: ["id", "name", "country_name", "country_code", "lat", "lon", "count"],
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
      boost: { name: 2 },
    },
  });
  citySearch.addAll(citiesData);

  // Organization search index
  orgSearch = new MiniSearch({
    fields: ["name"],
    storeFields: ["id", "name", "org_type", "count"],
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
    },
  });
  orgSearch.addAll(orgsData);
}

/** Rank suggestions the way a typeahead reader expects: among entries that match the
 *  query equally (same number of matched terms), the popular one comes first — "mozart"
 *  must offer Wolfgang Amadeus (16k concerts) above Leopold (44), and "paris" Paris FR
 *  above Paris TX. Text relevance still leads: a query like "leopold mozart" matches
 *  Leopold on two terms and W.A. on one, so Leopold outranks him regardless of counts. */
function rankByTermsThenCount<T extends { count: number }>(
  results: Array<{ terms?: string[]; count?: number }>,
  maxResults: number,
): T[] {
  return results
    .sort(
      (a, b) =>
        (b.terms?.length ?? 0) - (a.terms?.length ?? 0) || (b.count ?? 0) - (a.count ?? 0),
    )
    .slice(0, maxResults) as unknown as T[];
}

export function searchComposers(query: string, maxResults = 10): Composer[] {
  if (!composerSearch || !query.trim()) return [];
  return rankByTermsThenCount<Composer>(composerSearch.search(query), maxResults);
}

export function searchCities(query: string, maxResults = 10): City[] {
  if (!citySearch || !query.trim()) return [];
  return rankByTermsThenCount<City>(citySearch.search(query), maxResults);
}

export function searchOrgs(query: string, maxResults = 10): Org[] {
  if (!orgSearch || !query.trim()) return [];
  return rankByTermsThenCount<Org>(orgSearch.search(query), maxResults);
}
